import { noop } from '@/shared/utils/noop';
import type { TrialPhase } from '@/viewmodel/features/quota/types';

const DISMISSED_PHASES_ITEM = 'aleph.trialBanner.dismissed.v1';

const PHASES: readonly TrialPhase[] = ['active', 'urgent', 'expired'];

const isPhase = (value: unknown): value is TrialPhase =>
  typeof value === 'string' && PHASES.includes(value as TrialPhase);

export type DismissedPhases = Readonly<Record<string, TrialPhase>>;

const listeners = new Set<() => void>();
let cached: { raw: string | null; phases: DismissedPhases } = { raw: null, phases: {} };

function readRaw(): string | null {
  try {
    return localStorage.getItem(DISMISSED_PHASES_ITEM);
  } catch {
    return null;
  }
}

function parse(raw: string | null): DismissedPhases {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const phases: Record<string, TrialPhase> = {};
    for (const [workspaceId, phase] of Object.entries(parsed)) {
      if (isPhase(phase)) phases[workspaceId] = phase;
    }
    return phases;
  } catch {
    return {};
  }
}

export function readDismissedPhases(): DismissedPhases {
  const raw = readRaw();
  if (raw !== cached.raw) cached = { raw, phases: parse(raw) };
  return cached.phases;
}

export function subscribeDismissedPhases(listener: () => void): () => void {
  listeners.add(listener);

  return (): void => {
    listeners.delete(listener);
  };
}

export function writeDismissedPhase(workspaceId: string, phase: TrialPhase): DismissedPhases {
  const phases = { ...readDismissedPhases(), [workspaceId]: phase };
  try {
    localStorage.setItem(DISMISSED_PHASES_ITEM, JSON.stringify(phases));
  } catch {
    noop();
  }
  cached = { raw: readRaw(), phases };
  for (const listener of listeners) listener();
  return phases;
}
